import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NotebookPen, ChevronRight } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import Avatar from "@/components/common/Avatar";
import { listAllSessions } from "@/lib/db";
import { formatDateTime } from "@/lib/format";

import { useAuth } from "@/contexts/AuthContext";

export default function Sessions() {
  const { clinicId } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { 
    listAllSessions(clinicId).then(setList).finally(() => setLoading(false)); 
  }, [clinicId]);

  return (
    <div className="ds-fade-up">
      <PageHeader eyebrow="Histórico clínico" title="Todas as sessões"
                  description="Linha do tempo unificada das suas evoluções." />
      {loading ? (
        <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
      ) : list.length === 0 ? (
        <EmptyState icon={NotebookPen} title="Nenhuma sessão registrada"
                    description="Abra o prontuário de um paciente para registrar a primeira evolução." />
      ) : (
        <>
          {/* Mobile View: Cards */}
          <div className="grid grid-cols-1 gap-4 md:hidden" data-testid="sessions-cards">
            {list.map(s => {
              const p = s.patient || {};
              return (
                <Link to={p.id ? `/app/pacientes/${p.id}` : "#"} key={s.id} className="ds-card p-5 space-y-4 flex flex-col active:scale-[0.98] transition-transform">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar name={p.full_name || "?"} size={44} />
                      <div>
                        <div className="text-sm font-bold" style={{fontFamily:"'Outfit',sans-serif"}}>{p.full_name || "—"}</div>
                        <div className="text-[10px] text-[hsl(var(--text-tertiary))] uppercase font-bold tracking-tight">{formatDateTime(s.session_date)}</div>
                      </div>
                    </div>
                    {s.mood_scale && <span className="ds-badge ds-badge-info">Humor {s.mood_scale}</span>}
                  </div>
                  {s.assessment && (
                    <div className="text-sm text-[hsl(var(--text-secondary))] bg-[hsl(var(--bg-base))] p-3 rounded-xl line-clamp-2 italic">
                      "{s.assessment}"
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-[10px] text-[hsl(var(--text-tertiary))] font-bold uppercase tracking-widest">{s.duration_min} min de sessão</div>
                    <div className="text-[hsl(var(--brand))] text-xs font-bold flex items-center gap-1">Ver detalhes <ChevronRight className="w-3 h-3" /></div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Desktop View: List */}
          <div className="hidden md:block ds-card divide-y divide-[hsl(var(--border-divider))]" data-testid="sessions-list">
            {list.map(s => {
              const p = s.patient || {};
              return (
                <Link to={p.id ? `/app/pacientes/${p.id}` : "#"} key={s.id}
                      className="flex items-center gap-4 p-4 hover:bg-[hsl(var(--bg-hover))]/50 transition-colors">
                  <Avatar name={p.full_name || "?"} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{p.full_name || "—"}</div>
                    <div className="text-xs text-[hsl(var(--text-tertiary))]">{formatDateTime(s.session_date)} · {s.duration_min} min</div>
                    {s.assessment && <div className="text-xs text-[hsl(var(--text-secondary))] mt-1 line-clamp-1">{s.assessment}</div>}
                  </div>
                  {s.mood_scale && <span className="ds-badge ds-badge-info">Humor {s.mood_scale}/10</span>}
                  <ChevronRight className="w-4 h-4 text-[hsl(var(--text-tertiary))]" />
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
